import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, TextField, Button, Stack, Switch, FormControlLabel, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import { getNotificationTemplates, updateNotificationTemplate } from '../../api/endpoints/settings';

const DEFAULT_TEMPLATES = [
  { id: 'welcome', title: 'Welcome Notification', subject: 'Welcome to AstroPandit!', body: 'Thank you for joining AstroPandit. Start your spiritual journey today!', pushEnabled: true },
  { id: 'chat_request', title: 'Chat Request', subject: 'New chat request', body: 'You have a new chat request from {{userName}}.', pushEnabled: true },
  { id: 'chat_reply', title: 'Chat Reply', subject: 'New message received', body: '{{senderName}} sent you a message.', pushEnabled: true },
  { id: 'kyc_approved', title: 'KYC Approved', subject: 'KYC Verified', body: 'Your KYC documents have been approved. You can now start offering services.', pushEnabled: true },
  { id: 'kyc_rejected', title: 'KYC Rejected', subject: 'KYC Rejected', body: 'Your KYC was rejected. Reason: {{reason}}', pushEnabled: true },
  { id: 'order_shipped', title: 'Order Shipped', subject: 'Order Shipped', body: 'Your order {{orderNumber}} has been shipped!', pushEnabled: true },
  { id: 'payment_received', title: 'Payment Received', subject: 'Payment Confirmed', body: 'Payment of {{amount}} received successfully.', pushEnabled: true },
  { id: 'withdrawal', title: 'Withdrawal Update', subject: 'Withdrawal {{status}}', body: 'Your withdrawal request of {{amount}} has been {{status}}.', pushEnabled: true },
  { id: 'live_stream', title: 'Live Stream Reminder', subject: 'Live Now!', body: '{{astrologerName}} is live! Join the stream.', pushEnabled: true },
];

const NotificationSettings = () => {
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await getNotificationTemplates();
      const data = res.data.data || [];
      if (Array.isArray(data) && data.length) {
        setTemplates(data);
      }
    } catch {
      // use defaults
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (id, field, value) => {
    setTemplates((prev) => prev.map((t) => t.id === id ? { ...t, [field]: value } : t));
  };

  const handleSave = async () => {
    try {
      for (const t of templates) {
        await updateNotificationTemplate(t.id, t);
      }
      toast.success('Notification settings saved');
    } catch {
      toast.error('Failed to save');
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <PageHeader title="Notification Settings" subtitle="Manage in-app and push notification templates" />
      <Stack spacing={2}>
        {templates.map((t) => (
          <Card key={t.id}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle1" fontWeight={600}>{t.title}</Typography>
                <FormControlLabel control={<Switch checked={t.pushEnabled} onChange={(e) => handleChange(t.id, 'pushEnabled', e.target.checked)} />} label="Push" />
              </Stack>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField size="small" label="Subject" value={t.subject} onChange={(e) => handleChange(t.id, 'subject', e.target.value)} fullWidth />
                <TextField size="small" label="Body" value={t.body} onChange={(e) => handleChange(t.id, 'body', e.target.value)} fullWidth multiline rows={2} />
              </Stack>
            </CardContent>
          </Card>
        ))}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleSave}>Save Notification Settings</Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default NotificationSettings;
